/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  loginWithPassword,
  loginWithToken,
  signupWithPassword,
} from "../../../api";
import {
  LoadStatus,
  LoadingState,
  User,
  UserAccessToken,
} from "../../../types";
import { getFormattedDate } from "../../../utils";

export interface UserState {
  user?: User;
  accessToken?: string;
  loginStatus: LoadingState;
}

const initialState: UserState = {
  user: undefined,
  accessToken: undefined,
  loginStatus: { status: LoadStatus.NONE },
};

function getInitialState(): UserState {
  if (typeof window !== "undefined") {
    const data = localStorage.getItem("accessToken");
    if (data) {
      return {
        user: undefined,
        accessToken: data,
        loginStatus: { status: LoadStatus.NONE },
      };
    }
    return initialState;
  } else {
    return initialState;
  }
}

/** Actions */

export const login = createAsyncThunk(
  "user/login",
  async (args: {
    username: string;
    password: string;
  }): Promise<UserAccessToken> => {
    return await loginWithPassword(args.username, args.password);
  }
);

export const signup = createAsyncThunk(
  "user/signup",
  async (args: {
    name: string;
    email: string;
    password: string;
  }): Promise<UserAccessToken> => {
    return await signupWithPassword(args.name, args.password, args.email);
  }
);

export const verifyToken = createAsyncThunk(
  "user/verifyToken",
  async (token: string): Promise<UserAccessToken> => {
    return await loginWithToken(token);
  }
);

export const userSlice = createSlice({
  name: "user",
  initialState: getInitialState(),
  reducers: {
    logout: (state) => {
      state.user = undefined;
      state.accessToken = undefined;
      state.loginStatus = initialState.loginStatus;
    },
  },
  extraReducers: (builder) => {
    builder
      // login
      .addCase(login.pending, (state) => {
        state.loginStatus.status = LoadStatus.IN_PROGRESS;
        state.loginStatus.startedAt = getFormattedDate();
        state.loginStatus.error = undefined;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.accessToken = action.payload.accessToken;
        state.loginStatus.status = LoadStatus.DONE;
        state.loginStatus.endedAt = getFormattedDate();
        state.loginStatus.error = undefined;
      })
      .addCase(login.rejected, (state, action) => {
        state.loginStatus.status = LoadStatus.FAILED;
        state.loginStatus.failedAt = getFormattedDate();
        state.loginStatus.error = action.error.message;
        state.user = undefined;
        state.accessToken = undefined;
      })
      // signup
      .addCase(signup.pending, (state) => {
        state.loginStatus.status = LoadStatus.IN_PROGRESS;
        state.loginStatus.startedAt = getFormattedDate();
        state.loginStatus.error = undefined;
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.accessToken = action.payload.accessToken;
        state.loginStatus.status = LoadStatus.DONE;
        state.loginStatus.endedAt = getFormattedDate();
        state.loginStatus.error = undefined;
      })
      .addCase(signup.rejected, (state, action) => {
        state.loginStatus.status = LoadStatus.FAILED;
        state.loginStatus.failedAt = getFormattedDate();
        state.loginStatus.error = action.error.message;
        state.user = undefined;
        state.accessToken = undefined;
      })
      // verifyToken
      .addCase(verifyToken.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.accessToken = action.payload.accessToken;
        state.loginStatus.status = LoadStatus.DONE;
        state.loginStatus.endedAt = getFormattedDate();
        state.loginStatus.error = undefined;
      })
      .addCase(verifyToken.rejected, (state, action) => {
        state.loginStatus.status = LoadStatus.FAILED;
        state.loginStatus.failedAt = getFormattedDate();
        state.loginStatus.error = action.error.message;
        state.user = undefined;
        state.accessToken = undefined;
      });
  },
});

export const { logout } = userSlice.actions;

export default userSlice.reducer;
